import * as THREE from 'three';

type Options = {
    textureWidth: number;
    textureHeight: number;
    clipBias: number;
    alpha: number;
    time: number;
    waterNormals: THREE.TextureLoader;
    sunDirection: THREE.Vector3;
    sunColor: number;
    waterColor: number;
    eye: THREE.Vector3;
    distortionScale: number;
    side: THREE.Side;
    fog: boolean;

}
type Parameters = {
    minFilter: THREE.TextureFilter,
    magFilter: THREE.TextureFilter,
    format: THREE.PixelFormat,
    stencilBuffer: false
}

class Water extends THREE.Mesh implements THREE.Water {
    private options: Options;
    private textureWidth: number;
    private textureHeight: number;
    private clipBias: number;
    private alpha: number;
    private time: number;
    private normalSampler: THREE.TextureLoader | null;
    private sunDirection: THREE.Vector3 | null;
    private sunColor: THREE.Color ;
    private waterColor: THREE.Color;
    private eye: THREE.Vector3;
    private distortionScale: number;
    private side: THREE.Side;
    private fog: boolean;

    private mirrorPlane: THREE.Plane;
    private normal: THREE.Vector3;
    private mirrorWorldPosition: THREE.Vector3;
    private cameraWorldPosition: THREE.Vector3;
    private rotationMatrix: THREE.Matrix4;
    private lookAtPosition: THREE.Vector3;
    private clipPlane: THREE.Vector4;
    private view: THREE.Vector3;
    private target: THREE.Vector3;
    private q: THREE.Vector4;
    private textureMatrix: THREE.Matrix4;
    private mirrorCamera: THREE.PerspectiveCamera;
    private parameters: Parameters;
    private renderTarget: THREE.WebGLRenderTarget;
    material: THREE.ShaderMaterialExtended;

    constructor(geometry: THREE.PlaneBufferGeometry, options: any) {
        super(geometry);
        this.options = options || {};
        this.textureWidth = this.options.textureWidth !== undefined ? this.options.textureWidth : 512
        this.textureHeight = this.options.textureHeight !== undefined ? this.options.textureHeight : 512;

        this.clipBias = this.options.clipBias !== undefined ? this.options.clipBias : 0.0;
        this.alpha = this.options.alpha !== undefined ? this.options.alpha : 1.0;
        this.time = this.options.time !== undefined ? this.options.time : 0.0;
        this.normalSampler = this.options.waterNormals !== undefined ? this.options.waterNormals : null;
        this.sunDirection = this.options.sunDirection !== undefined ? this.options.sunDirection : new THREE.Vector3( 0.70707, 0.70707, 0.0 );
        this.sunColor = new THREE.Color( this.options.sunColor !== undefined ? this.options.sunColor : 0xffffff );
        this.waterColor = new THREE.Color( this.options.waterColor !== undefined ? this.options.waterColor : 0x7F7F7F );
        this.eye = this.options.eye !== undefined ? this.options.eye : new THREE.Vector3( 0, 0, 0 );
        this.distortionScale = this.options.distortionScale !== undefined ? this.options.distortionScale : 20.0;
        this.side = this.options.side !== undefined ? this.options.side : THREE.FrontSide;
        this.fog = this.options.fog !== undefined ? this.options.fog : false;

        this.mirrorPlane = new THREE.Plane();
        this.normal = new THREE.Vector3();
        this.mirrorWorldPosition = new THREE.Vector3();
        this.cameraWorldPosition = new THREE.Vector3();
        this.rotationMatrix = new THREE.Matrix4();
        this.lookAtPosition = new THREE.Vector3( 0, 0, - 1 );
        this.clipPlane = new THREE.Vector4();

        this.view = new THREE.Vector3();
        this.target = new THREE.Vector3();
        this.q = new THREE.Vector4();

        this.textureMatrix = new THREE.Matrix4();

        this.mirrorCamera = new THREE.PerspectiveCamera();

        this.parameters = {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBFormat,
            stencilBuffer: false
        };

        this.renderTarget = new THREE.WebGLRenderTarget( this.textureWidth, this.textureHeight, this.parameters );

        if ( ! THREE.Math.isPowerOfTwo( this.textureWidth ) || ! THREE.Math.isPowerOfTwo( this.textureHeight ) ) {
            this.renderTarget.texture.generateMipmaps = false;
        }

        var mirrorShader = {

            uniforms: THREE.UniformsUtils.merge( [
                THREE.UniformsLib[ 'fog' ],
                THREE.UniformsLib[ 'lights' ],
                {
                    normalSampler: { value: null },
                    mirrorSampler: { value: null },
                    alpha: { value: 1.0 },
                    time: { value: 0.0 },
                    size: { value: 1.0 },
                    distortionScale: { value: 20.0 },
                    textureMatrix: { value: new THREE.Matrix4() },
                    sunColor: { value: new THREE.Color( 0x7F7F7F ) },
                    sunDirection: { value: new THREE.Vector3( 0.70707, 0.70707, 0 ) },
                    eye: { value: new THREE.Vector3() },
                    waterColor: { value: new THREE.Color( 0x555555 ) }
                }
            ] ) as THREE.ShaderMaterialExtended,

            vertexShader: [
                'uniform mat4 textureMatrix;',
                'uniform float time;',

                'varying vec4 mirrorCoord;',
                'varying vec4 worldPosition;',

                THREE.ShaderChunk[ 'fog_pars_vertex' ],
                THREE.ShaderChunk[ 'shadowmap_pars_vertex' ],

                'void main() {',
                '	mirrorCoord = modelMatrix * vec4( position, 1.0 );',
                '	worldPosition = mirrorCoord.xyzw;',
                '	mirrorCoord = textureMatrix * mirrorCoord;',
                '	vec4 mvPosition =  modelViewMatrix * vec4( position, 1.0 );',
                '	gl_Position = projectionMatrix * mvPosition;',

                THREE.ShaderChunk[ 'fog_vertex' ],
                THREE.ShaderChunk[ 'shadowmap_vertex' ],

                '}'
            ].join( '\n' ),

            fragmentShader: [
                'uniform sampler2D mirrorSampler;',
                'uniform float alpha;',
                'uniform float time;',
                'uniform float size;',
                'uniform float distortionScale;',
                'uniform sampler2D normalSampler;',
                'uniform vec3 sunColor;',
                'uniform vec3 sunDirection;',
                'uniform vec3 eye;',
                'uniform vec3 waterColor;',

                'varying vec4 mirrorCoord;',
                'varying vec4 worldPosition;',

                'vec4 getNoise( vec2 uv ) {',
                '	vec2 uv0 = ( uv / 103.0 ) + vec2(time / 17.0, time / 29.0);',
                '	vec2 uv1 = uv / 107.0-vec2( time / -19.0, time / 31.0 );',
                '	vec2 uv2 = uv / vec2( 8907.0, 9803.0 ) + vec2( time / 101.0, time / 97.0 );',
                '	vec2 uv3 = uv / vec2( 1091.0, 1027.0 ) - vec2( time / 109.0, time / -113.0 );',
                '	vec4 noise = texture2D( normalSampler, uv0 ) +',
                '		texture2D( normalSampler, uv1 ) +',
                '		texture2D( normalSampler, uv2 ) +',
                '		texture2D( normalSampler, uv3 );',
                '	return noise * 0.5 - 1.0;',
                '}',

                'void sunLight( const vec3 surfaceNormal, const vec3 eyeDirection, float shiny, float spec, float diffuse, inout vec3 diffuseColor, inout vec3 specularColor ) {',
                '	vec3 reflection = normalize( reflect( -sunDirection, surfaceNormal ) );',
                '	float direction = max( 0.0, dot( eyeDirection, reflection ) );',
                '	specularColor += pow( direction, shiny ) * sunColor * spec;',
                '	diffuseColor += max( dot( sunDirection, surfaceNormal ), 0.0 ) * sunColor * diffuse;',
                '}',

                THREE.ShaderChunk[ 'common' ],
                THREE.ShaderChunk[ 'packing' ],
                THREE.ShaderChunk[ 'bsdfs' ],
                THREE.ShaderChunk[ 'fog_pars_fragment' ],
                THREE.ShaderChunk[ 'lights_pars_begin' ],
                THREE.ShaderChunk[ 'shadowmap_pars_fragment' ],
                THREE.ShaderChunk[ 'shadowmask_pars_fragment' ],

                'void main() {',
                '	vec4 noise = getNoise( worldPosition.xz * size );',
                '	vec3 surfaceNormal = normalize( noise.xzy * vec3( 1.5, 1.0, 1.5 ) );',

                '	vec3 diffuseLight = vec3(0.0);',
                '	vec3 specularLight = vec3(0.0);',

                '	vec3 worldToEye = eye-worldPosition.xyz;',
                '	vec3 eyeDirection = normalize( worldToEye );',
                '	sunLight( surfaceNormal, eyeDirection, 100.0, 2.0, 0.5, diffuseLight, specularLight );',

                '	float distance = length(worldToEye);',

                '	vec2 distortion = surfaceNormal.xz * ( 0.001 + 1.0 / distance ) * distortionScale;',
                '	vec3 reflectionSample = vec3( texture2D( mirrorSampler, mirrorCoord.xy / mirrorCoord.z + distortion ) );',

                '	float theta = max( dot( eyeDirection, surfaceNormal ), 0.0 );',
                '	float rf0 = 0.3;',
                '	float reflectance = rf0 + ( 1.0 - rf0 ) * pow( ( 1.0 - theta ), 5.0 );',
                '	vec3 scatter = max( 0.0, dot( surfaceNormal, eyeDirection ) ) * waterColor;',
                '	vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatter ) * getShadowMask(), ( vec3( 0.1 ) + reflectionSample * 0.9 + reflectionSample * specularLight ), reflectance);',
                '	vec3 outgoingLight = albedo;',
                '	gl_FragColor = vec4( outgoingLight, alpha );',

                THREE.ShaderChunk[ 'tonemapping_fragment' ],
                THREE.ShaderChunk[ 'fog_fragment' ],

                '}'
            ].join( '\n' )

        };

        var material = new THREE.ShaderMaterial( {
            fragmentShader: mirrorShader.fragmentShader,
            vertexShader: mirrorShader.vertexShader,
            uniforms: THREE.UniformsUtils.clone( mirrorShader.uniforms ),
            transparent: true,
            lights: true,
            side: this.side,
            fog: this.fog
        } ) as THREE.ShaderMaterialExtended;

        material.uniforms.mirrorSampler.value = this.renderTarget.texture;
        material.uniforms.textureMatrix.value = this.textureMatrix;
        material.uniforms.alpha.value = this.alpha;
        material.uniforms.time.value = this.time;
        material.uniforms.normalSampler.value = this.normalSampler;
        material.uniforms.sunColor.value = this.sunColor;
        material.uniforms.waterColor.value = this.waterColor;
        material.uniforms.sunDirection.value = this.sunDirection;
        material.uniforms.distortionScale.value = this.distortionScale;

        material.uniforms.eye.value = this.eye;

        this.material = material;

    }

    onBeforeRender = (renderer: THREE.WebGLRenderer, scene: THREE.Scene , camera: THREE.PerspectiveCamera) => {
        this.mirrorWorldPosition.setFromMatrixPosition( this.matrixWorld );
        this.cameraWorldPosition.setFromMatrixPosition( camera.matrixWorld );

        this.rotationMatrix.extractRotation( this.matrixWorld );

        this.normal.set( 0, 0, 1 );
        this.normal.applyMatrix4( this.rotationMatrix );

        this.view.subVectors( this.mirrorWorldPosition, this.cameraWorldPosition );

        // Avoid rendering when mirror is facing away

        if ( this.view.dot( this.normal ) > 0 ) return;

        this.view.reflect( this.normal ).negate();
        this.view.add( this.mirrorWorldPosition );

        this.rotationMatrix.extractRotation( camera.matrixWorld );

        this.lookAtPosition.set( 0, 0, - 1 );
        this.lookAtPosition.applyMatrix4( this.rotationMatrix );
        this.lookAtPosition.add( this.cameraWorldPosition );

        this.target.subVectors( this.mirrorWorldPosition, this.lookAtPosition );
        this.target.reflect( this.normal ).negate();
        this.target.add( this.mirrorWorldPosition );

        this.mirrorCamera.position.copy( this.view );
        this.mirrorCamera.up.set( 0, 1, 0 );
        this.mirrorCamera.up.applyMatrix4( this.rotationMatrix );
        this.mirrorCamera.up.reflect( this.normal );
        this.mirrorCamera.lookAt( this.target );

        this.mirrorCamera.far = camera.far; // Used in WebGLBackground

        this.mirrorCamera.updateMatrixWorld(false);
        this.mirrorCamera.projectionMatrix.copy( camera.projectionMatrix );

        // Update the texture matrix
        this.textureMatrix.set(
            0.5, 0.0, 0.0, 0.5,
            0.0, 0.5, 0.0, 0.5,
            0.0, 0.0, 0.5, 0.5,
            0.0, 0.0, 0.0, 1.0
        );
        this.textureMatrix.multiply( this.mirrorCamera.projectionMatrix );
        this.textureMatrix.multiply( this.mirrorCamera.matrixWorldInverse );

        // Now update projection matrix with new clip plane, implementing code from: http://www.terathon.com/code/oblique.html
        // Paper explaining this technique: http://www.terathon.com/lengyel/Lengyel-Oblique.pdf
        this.mirrorPlane.setFromNormalAndCoplanarPoint( this.normal, this.mirrorWorldPosition );
        this.mirrorPlane.applyMatrix4( this.mirrorCamera.matrixWorldInverse );

        this.clipPlane.set( this.mirrorPlane.normal.x, this.mirrorPlane.normal.y, this.mirrorPlane.normal.z, this.mirrorPlane.constant );

        var projectionMatrix = this.mirrorCamera.projectionMatrix;

        this.q.x = ( Math.sign( this.clipPlane.x ) + projectionMatrix.elements[ 8 ] ) / projectionMatrix.elements[ 0 ];
        this.q.y = ( Math.sign( this.clipPlane.y ) + projectionMatrix.elements[ 9 ] ) / projectionMatrix.elements[ 5 ];
        this.q.z = - 1.0;
        this.q.w = ( 1.0 + projectionMatrix.elements[ 10 ] ) / projectionMatrix.elements[ 14 ];

        // Calculate the scaled plane vector
        this.clipPlane.multiplyScalar( 2.0 / this.clipPlane.dot( this.q ) );

        // Replacing the third row of the projection matrix
        projectionMatrix.elements[ 2 ] = this.clipPlane.x;
        projectionMatrix.elements[ 6 ] = this.clipPlane.y;
        projectionMatrix.elements[ 10 ] = this.clipPlane.z + 1.0 - this.clipBias;
        projectionMatrix.elements[ 14 ] = this.clipPlane.w;

        this.eye.setFromMatrixPosition( camera.matrixWorld );

        var currentRenderTarget = renderer.getRenderTarget();

        var currentVrEnabled = renderer.vr.enabled;
        var currentShadowAutoUpdate = renderer.shadowMap.autoUpdate;

        this.visible = false;

        renderer.vr.enabled = false; // Avoid camera modification and recursion
        renderer.shadowMap.autoUpdate = false; // Avoid re-computing shadows

        renderer.render( scene, this.mirrorCamera, this.renderTarget, true );

        this.visible = true;

        renderer.vr.enabled = currentVrEnabled;
        renderer.shadowMap.autoUpdate = currentShadowAutoUpdate;

        renderer.setRenderTarget( currentRenderTarget );

    };

    toJSON(meta?: any): any {
        return {};
    }
}

export default Water;


// WEBPACK FOOTER //
// ./src/components/Home/Water.tsx